import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck2, MessageCircle, Video } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useSiteData } from "@/hooks/useSite";
import { LineReveal, Reveal, FadeIn, ParallaxImage } from "@/components/Motion";
import ServiceCard from "@/components/ServiceCard";
import { buildInfoMessage, whatsappUrl } from "@/lib/site";

export default function Servizi() {
  const { settings: seoSettings } = useSiteData();
  const seo = seoSettings.seo?.servizi || {};
  useSEO({
    title: seo.title || "Servizi — Visita nutrizionale a Salerno | K Nutrition",
    description:
      seo.description ||
      "Prima visita nutrizionale completa, controlli, check composizione corporea con BIA e consulenze online: scopri i servizi di K Nutrition, Dott.ssa Francesca Notarfrancesco, Biologa Nutrizionista a Salerno. Richiedi informazioni e appuntamenti su WhatsApp.",
    path: "/servizi",
  });

  const { settings, services } = useSiteData();
  const svc = settings.content.services;

  const studio = services.filter((s) => s.mode === "studio" && s.active !== false);
  const online = services.filter((s) => s.mode === "online" && s.active !== false);

  return (
    <>
      <section className="pt-28 lg:pt-36 pb-14 lg:pb-20 overflow-hidden" data-testid="services-header">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeIn>
              <p className="flex items-center gap-3 text-[11px] uppercase tracking-widest2 text-deeptaupe font-medium">
                <span className="w-10 h-px bg-taupe inline-block" aria-hidden="true" />
                Servizi
              </p>
            </FadeIn>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight mt-6 mb-8">
              <LineReveal delay={0.2}>{svc.title}</LineReveal>
            </h1>
            <FadeIn delay={0.5}>
              <p className="text-base lg:text-lg leading-relaxed text-charcoal/75 max-w-2xl">{svc.text}</p>
            </FadeIn>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <FadeIn delay={0.25}>
              <div className="relative max-w-[420px] mx-auto">
                <div aria-hidden="true" className="absolute -bottom-6 -left-6 w-40 h-40 rounded-full bg-peach -z-10" />
                <div className="overflow-hidden rounded-t-[999px] rounded-b-[24px] aspect-[3/4]">
                  <ParallaxImage
                    src={settings.photos.services}
                    alt="Dott.ssa Francesca Notarfrancesco di schiena, con un broccoli sollevato e uno dietro la schiena"
                    className="w-full h-full"
                  />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24" data-testid="services-studio">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl lg:text-4xl mb-8 lg:mb-12 flex items-center gap-4">
              In studio
              <span className="text-[11px] uppercase tracking-widest2 font-sans font-medium text-taupe">
                Via Santi Martiri Salernitani 15, Salerno
              </span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {studio.map((s, i) => (
              <ServiceCard key={s.id} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 lg:pb-24" data-testid="services-online">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl lg:text-4xl mb-8 lg:mb-12 flex items-center gap-4">
              Online
              <Video size={26} strokeWidth={1.4} className="text-taupe" />
              <span className="text-[11px] uppercase tracking-widest2 font-sans font-medium text-taupe">
                Da casa tua, con la stessa cura
              </span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {online.map((s, i) => (
              <ServiceCard key={s.id} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="services-how">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-12">
          <Reveal>
            <div className="bg-charcoal text-ivory rounded-[36px] p-8 lg:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <h2 className="font-display text-3xl lg:text-5xl leading-tight">
                  Come funziona la <em className="text-sand">richiesta</em>
                </h2>
                <p className="mt-5 text-ivory/70 text-sm lg:text-base leading-relaxed">
                  Scegli il servizio, indica data e orario preferiti e invia la richiesta su WhatsApp: la
                  prenotazione è confermata solo dallo studio.
                </p>
                <Link
                  to="/prenota"
                  data-testid="services-cta-button"
                  className="mt-8 inline-flex items-center gap-3 rounded-full bg-peach text-charcoal text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:bg-ivory"
                >
                  Richiedi un appuntamento <ArrowRight size={16} />
                </Link>
                <a
                  href={whatsappUrl(settings.contact.whatsapp, buildInfoMessage(null))}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="services-info-whatsapp"
                  className="mt-8 sm:ml-4 inline-flex items-center gap-3 rounded-full border border-ivory/30 text-ivory text-sm uppercase tracking-widest2 font-medium px-8 py-4 transition-all duration-300 hover:bg-ivory/10"
                >
                  <MessageCircle size={16} /> Richiedi informazioni
                </a>
              </div>
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: MessageCircle, t: "1 · Scegli il servizio", d: "Seleziona la visita o la consulenza più adatta a te." },
                  { icon: CalendarCheck2, t: "2 · Data e orario", d: "Indica le tue preferenze tra gli orari dello studio." },
                  { icon: MessageCircle, t: "3 · WhatsApp", d: "Un messaggio precompilato è pronto da inviare: premi Invio tu." },
                  { icon: MessageCircle, t: "4 · Conferma", d: "K Nutrition risponde su WhatsApp confermando la disponibilità." },
                ].map((step) => (
                  <div key={step.t} className="border border-ivory/15 rounded-2xl p-6" data-testid="services-how-step">
                    <step.icon size={20} strokeWidth={1.4} className="text-sand mb-4" />
                    <p className="font-display text-xl mb-2">{step.t}</p>
                    <p className="text-[13px] text-ivory/65 leading-relaxed">{step.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
